import { useState } from "react";
import { Star, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primaryDark: "#08263e",
  primaryRed: "#ec474e",
  secondaryBlue: "#518dca",
};

export const SurveyModal = ({ sessionId, userName, onClose, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const labels = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];

  const handleSubmit = async () => {
    if (rating === 0) { toast.error("Please select a rating"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/chat/survey`, {
        session_id: sessionId,
        rating,
        comment: comment.trim(),
        user_name: userName || "",
      });
      toast.success("Salamat sa feedback mo, lods!");
      onSubmitted?.();
      onClose();
    } catch (err) {
      if (err.response?.data?.detail?.includes("already submitted")) {
        toast.info("You already submitted feedback for this session");
        onClose();
      } else {
        toast.error("Failed to submit survey");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="survey-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="p-4 text-center" style={{ background: `linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.secondaryBlue})` }}>
          <button onClick={onClose} className="absolute top-3 right-3 text-white/60 hover:text-white" data-testid="survey-close">
            <X size={18} />
          </button>
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
            <Star size={24} className="text-yellow-300" />
          </div>
          <h3 className="text-white font-bold text-base">Rate Your Experience</h3>
          <p className="text-white/70 text-xs mt-1">Kumusta ang chat natin?</p>
        </div>

        {/* Rating Stars */}
        <div className="p-5">
          <div className="flex justify-center gap-2 mb-2" data-testid="survey-stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHovered(star)}
                onMouseLeave={() => setHovered(0)}
                className="transition-transform hover:scale-110"
                data-testid={`survey-star-${star}`}
              >
                <Star
                  size={32}
                  className={`transition-colors ${
                    star <= (hovered || rating)
                      ? "text-yellow-400 fill-yellow-400"
                      : "text-gray-300"
                  }`}
                />
              </button>
            ))}
          </div>
          <p className="text-center text-sm font-medium h-5" style={{ color: rating ? COLORS.primaryDark : "#999" }}>
            {rating ? labels[rating] : "Tap a star to rate"}
          </p>

          {/* Comment */}
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Any comments or suggestions? (optional)"
            className="w-full mt-3 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 resize-none"
            style={{ focusRingColor: COLORS.secondaryBlue }}
            rows={3}
            maxLength={500}
            data-testid="survey-comment"
          />
          <p className="text-right text-[10px] text-[#999] mt-0.5">{comment.length}/500</p>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={rating === 0 || submitting}
            className="w-full mt-3 text-white rounded-lg"
            style={{ backgroundColor: COLORS.primaryDark }}
            data-testid="survey-submit"
          >
            <Send size={14} className="mr-1.5" />
            {submitting ? "Submitting..." : "Submit Feedback"}
          </Button>

          <button
            onClick={onClose}
            className="w-full mt-2 text-xs text-[#999] hover:text-[#666] py-1"
            data-testid="survey-skip"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};
